import React, { useCallback, useEffect, useRef, useState } from 'react';
import TemplateGallery from '../components/shared/templates/TemplateGallery';
import TemplateFieldsDialog from '../components/shared/templates/TemplateFieldsDialog';
import { templatesApi } from './templatesApi';

/**
 * "Insert template" toolbar button shared by the five editors (BBD-108).
 *
 * The editors memoize their Quill `modules` with an empty dependency list,
 * so the toolbar handler cannot close over React state. Instead the handler
 * (`insertTemplateToolbarHandler`) captures the selection and fires a window
 * event carrying the Quill instance; the hook in the owning component picks
 * it up when the instance matches its own editor.
 *
 * Wiring in an editor:
 *   1. add `["insert-template"]` to the toolbar container and
 *      `"insert-template": insertTemplateToolbarHandler` to `handlers`;
 *   2. in the icon-injection effect call `decorateInsertTemplateButton(toolbar)`;
 *   3. `const snippets = useTemplateSnippets(quillRef, { workspaceId });`
 *      and render `{snippets.element}`.
 */

export const INSERT_TEMPLATE_EVENT = 'bb:insert-template';

export function insertTemplateToolbarHandler() {
    const quill = this.quill;
    const selection = quill.getSelection(true) || { index: quill.getLength() - 1, length: 0 };
    window.dispatchEvent(new CustomEvent(INSERT_TEMPLATE_EVENT, { detail: { quill, selection } }));
}

export function decorateInsertTemplateButton(toolbar) {
    const button = toolbar?.container?.querySelector('.ql-insert-template');
    if (button) {
        button.innerHTML = '<i class="fas fa-puzzle-piece"></i>';
        button.title = 'Insert template';
        button.setAttribute('aria-label', 'Insert template');
    }
}

/** True when the Quill selection sits inside a quill-table-better cell. */
function selectionInTable(quill, index) {
    try {
        const [leaf] = quill.getLeaf(index);
        let node = leaf?.domNode;
        while (node && node !== quill.root) {
            if (node.tagName === 'TD' || node.tagName === 'TH') return true;
            node = node.parentNode;
        }
    } catch (e) {
        /* fall through */
    }
    return false;
}

export default function useTemplateSnippets(quillRef, { workspaceId = null } = {}) {
    const [open, setOpen] = useState(false);
    const [pending, setPending] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const selectionRef = useRef(null);
    const quillInstanceRef = useRef(null);

    useEffect(() => {
        const onInsert = (e) => {
            const mine = quillRef?.current?.getEditor?.();
            if (!mine || e.detail?.quill !== mine) return;
            quillInstanceRef.current = mine;
            selectionRef.current = e.detail.selection;
            if (selectionInTable(mine, e.detail.selection.index)) {
                window.alert('Move the cursor out of the table first, then insert the template.');
                return;
            }
            setError('');
            setOpen(true);
        };
        window.addEventListener(INSERT_TEMPLATE_EVENT, onInsert);
        return () => window.removeEventListener(INSERT_TEMPLATE_EVENT, onInsert);
    }, [quillRef]);

    const insertRendered = useCallback((html) => {
        const quill = quillInstanceRef.current || quillRef?.current?.getEditor?.();
        if (!quill) return;
        const sel = selectionRef.current || quill.getSelection(true) || { index: quill.getLength() - 1, length: 0 };
        let index = sel.index;
        if (sel.length) {
            quill.deleteText(index, sel.length, 'user');
        }
        // Start the snippet on its own line so headings don't merge into the current block.
        const before = index > 0 ? quill.getText(index - 1, 1) : '\n';
        if (before !== '\n') {
            quill.insertText(index, '\n', 'user');
            index += 1;
        }
        // 'user' source lands the paste in undo history (BBD-108).
        quill.clipboard.dangerouslyPasteHTML(index, html, 'user');
        const after = quill.getSelection();
        quill.setSelection(after ? after.index : index, 0, 'silent');
        quill.focus();
    }, [quillRef]);

    const finish = useCallback(async (template, values) => {
        setBusy(true);
        setError('');
        try {
            const rendered = await templatesApi.render({ ref: template.ref, values, workspaceId });
            setPending(null);
            insertRendered(rendered.content || '');
        } catch (e) {
            setError(e.message || 'Could not insert the template');
        } finally {
            setBusy(false);
        }
    }, [workspaceId, insertRendered]);

    const choose = useCallback((template) => {
        setOpen(false);
        if (template.fields && template.fields.length) setPending(template);
        else finish(template, {});
    }, [finish]);

    const element = (
        <>
            <TemplateGallery
                isOpen={open}
                onClose={() => setOpen(false)}
                onChoose={choose}
                workspaceId={workspaceId}
                kind="snippet"
                title="Insert a snippet"
                chooseLabel="Insert"
                compact
            />
            {pending && (
                <TemplateFieldsDialog
                    template={pending}
                    workspaceId={workspaceId}
                    submitLabel="Insert"
                    busy={busy}
                    error={error}
                    onSubmit={(values) => finish(pending, values)}
                    onCancel={() => { if (!busy) { setPending(null); setError(''); } }}
                />
            )}
        </>
    );

    return { openPicker: () => setOpen(true), insertRendered, element };
}
